import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Code2,
  FileCheck,
  Loader2,
  PlayCircle,
  Sparkles,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import {
  listSkills,
  runSkill,
  type SkillCatalogEntry,
  type SkillRunResponse,
} from "@/lib/api";
import { classifyApiError, formatApiDebug } from "@/hooks/useApiErrorState";

// ---------------------------------------------------------------------------
// Templates de payload par skill — alignes sur les contrats documentes
// dans backend/services/skills/*.py. Permettent au user de demarrer
// rapidement sans avoir a memoriser le format de chaque skill.
// ---------------------------------------------------------------------------

const SKILL_TEMPLATES: Record<
  string,
  { label: string; description: string; payload: object }
> = {
  "chronology-extract": {
    label: "Extraction chronologique",
    description:
      "Detecte les dates ISO 8601 dans un texte (MVP regex, sans LLM).",
    payload: {
      text:
        "Le 2026-01-15 le tribunal a recu la requete. Le 2026-02-03 les parties ont depose leurs memoires.",
    },
  },
  "intake-dossier": {
    label: "Synthese d'un memoire",
    description:
      "Extraction structuree (parties, objet, sections, demandes) — regex heuristique, sera rebranchee Mistral en PR ulterieure.",
    payload: {
      text:
        "MEMOIRE EN DEMANDE\n\nDEMANDEUR : ALPHA SARL\nDEFENDEUR : BETA SAS\n\nOBJET DU LITIGE : retard de livraison.\n\nI. EXPOSE DES FAITS\n...\n\nPAR CES MOTIFS,\n- CONDAMNER BETA a payer 10 000 000 FCFA\n- CONDAMNER BETA aux depens",
    },
  },
  "section-drafting": {
    label: "Redaction d'une section de sentence",
    description:
      "Genere une section via les templates AUA 2017 (identification_parties, designation_arbitres, date_lieu, dispositif, etc.).",
    payload: {
      section_type: "identification_parties",
      parties: [
        { name: "Societe ALPHA SARL", role: "demandeur" },
        { name: "Societe BETA SAS", role: "defendeur" },
      ],
    },
  },
  "canvas-generate": {
    label: "Canevas complet de sentence",
    description:
      "Genere le canevas Art. 20 AUA complet (8 sections obligatoires) — produit un Artifact `award_draft`.",
    payload: {
      case_reference: "GSF-2026-001",
      parties: [
        { name: "Societe ALPHA SARL", role: "demandeur" },
        { name: "Societe BETA SAS", role: "defendeur" },
      ],
      arbitres: [
        { name: "Aminata Ndiaye", role: "arbitre_unique" },
      ],
      lieu_arbitrage: "Abidjan",
      langue: "français",
    },
  },
  "validate-art20": {
    label: "Controle conformite Art. 20 AUA",
    description:
      "Verifie qu'une sentence en cours contient les mentions obligatoires Art. 20 AUA 2017.",
    payload: {
      content_sections: {
        identification_parties:
          "Le tribunal arbitral, statuant entre ALPHA SARL et BETA SAS, ...",
        designation_arbitres:
          "Mme Aminata Ndiaye, arbitre unique designee par les parties, ...",
        date_lieu_sentence:
          "Sentence rendue a Abidjan, le 15 avril 2026. Langue de la procedure : français.",
        resume_pretentions:
          "Le demandeur sollicite la condamnation du defendeur a payer 10M FCFA ...",
        resume_moyens:
          "Au soutien de ses pretentions, le demandeur invoque la mauvaise execution du contrat ...",
        expose_procedure:
          "La procedure s'est deroulee selon les etapes suivantes : ...",
        motivation:
          "Attendu que le defendeur n'a pas respecte les delais de livraison fixes au contrat ...",
        dispositif:
          "PAR CES MOTIFS, le Tribunal arbitral, statuant contradictoirement, CONDAMNE ...",
      },
    },
  },
  "calculate-fees": {
    label: "Calcul des frais CCJA",
    description:
      "Bareme Decision n° 004/99/CCJA (Annexes I/II/III) — calcul deterministe avec enveloppe d'audit.",
    payload: {
      montant_litige: 10000000,
      devise: "XOF",
      nombre_arbitres: 1,
    },
  },
};

// Icone par skill — pure decoration UI.
const SKILL_ICONS: Record<string, React.ReactNode> = {
  "chronology-extract": <Sparkles size={14} />,
  "intake-dossier": <Wand2 size={14} />,
  "section-drafting": <Code2 size={14} />,
  "canvas-generate": <Code2 size={14} />,
  "validate-art20": <FileCheck size={14} />,
  "calculate-fees": <FileCheck size={14} />,
};

// ---------------------------------------------------------------------------

interface SkillRunnerProps {
  caseId: number;
}

type CatalogState =
  | { kind: "loading" }
  | { kind: "ready"; skills: SkillCatalogEntry[] }
  | { kind: "error"; detail: string; debug?: string };

type RunState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "success"; result: SkillRunResponse }
  | { kind: "error"; detail: string; debug?: string };

export function SkillRunner({ caseId }: SkillRunnerProps) {
  const [catalog, setCatalog] = useState<CatalogState>({ kind: "loading" });
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
  const [payloadText, setPayloadText] = useState<string>("");
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const [run, setRun] = useState<RunState>({ kind: "idle" });

  // --- Charge le catalog au mount + a chaque changement de dossier ---
  useEffect(() => {
    let cancelled = false;
    setCatalog({ kind: "loading" });
    listSkills(caseId)
      .then((skills) => {
        if (cancelled) return;
        setCatalog({ kind: "ready", skills });
      })
      .catch((err) => {
        if (cancelled) return;
        const classified = classifyApiError(err);
        setCatalog({
          kind: "error",
          detail:
            classified.kind !== "none"
              ? classified.detail
              : "Erreur inattendue.",
          debug:
            classified.kind !== "none"
              ? formatApiDebug(classified.debug)
              : undefined,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  // --- Prefille le payload textarea quand on choisit une skill ---
  const onSelectSkill = useCallback((name: string) => {
    setSelectedSkill(name);
    const template = SKILL_TEMPLATES[name];
    setPayloadText(
      template ? JSON.stringify(template.payload, null, 2) : "{}",
    );
    setPayloadError(null);
    setRun({ kind: "idle" });
  }, []);

  // --- Validation locale du JSON avant POST ---
  type ParsedPayload =
    | null
    | { kind: "ok"; value: Record<string, unknown> }
    | { kind: "err"; error: string };
  const parsedPayload: ParsedPayload = useMemo(() => {
    if (!payloadText.trim()) return null;
    try {
      const obj = JSON.parse(payloadText);
      if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
        return { kind: "err", error: "Le payload doit etre un objet JSON {...}." };
      }
      return { kind: "ok", value: obj as Record<string, unknown> };
    } catch (e) {
      return {
        kind: "err",
        error:
          e instanceof Error
            ? `JSON invalide : ${e.message}`
            : "JSON invalide.",
      };
    }
  }, [payloadText]);

  const onExecute = useCallback(async () => {
    if (!selectedSkill || !parsedPayload) return;
    if (parsedPayload.kind === "err") {
      setPayloadError(parsedPayload.error);
      return;
    }
    setPayloadError(null);
    setRun({ kind: "running" });
    try {
      const result = await runSkill(caseId, selectedSkill, parsedPayload.value);
      setRun({ kind: "success", result });
      const hasBlocking = result.blocking_issues.length > 0;
      if (hasBlocking) {
        toast.warning(
          `Skill executee avec ${result.blocking_issues.length} blocage(s) — voir le panneau de resultat.`,
        );
      } else {
        toast.success(
          `Artifact #${result.artifact_id} cree (${result.artifact_type}).`,
        );
      }
    } catch (err) {
      const classified = classifyApiError(err);
      const detail =
        classified.kind !== "none"
          ? classified.detail
          : "Erreur inattendue.";
      const debug =
        classified.kind !== "none"
          ? formatApiDebug(classified.debug)
          : undefined;
      setRun({ kind: "error", detail, debug });
      toast.error(detail);
    }
  }, [caseId, selectedSkill, parsedPayload]);

  const selectedTemplate = selectedSkill
    ? SKILL_TEMPLATES[selectedSkill]
    : null;

  return (
    <div className="space-y-4">
      {/* --- Selection skill --- */}
      <div className="space-y-2">
        <Label htmlFor="skill-runner-select">Skill a executer</Label>
        {catalog.kind === "loading" && (
          <div
            className="flex items-center gap-2 text-xs"
            style={{ color: "var(--arbitre-slate-300)" }}
          >
            <Loader2 size={12} className="animate-spin" />
            Chargement du catalog…
          </div>
        )}
        {catalog.kind === "error" && (
          <div
            className="gsf-surface p-3 text-xs"
            style={{ color: "var(--reject-red)" }}
          >
            <div className="flex items-center gap-2 font-medium">
              <AlertCircle size={14} /> Impossible de charger le catalog.
            </div>
            <div className="mt-1 ml-6">{catalog.detail}</div>
            {catalog.debug && (
              <div
                className="mt-1 ml-6 gsf-mono-proof opacity-70"
                style={{ fontSize: "0.7rem" }}
              >
                {catalog.debug}
              </div>
            )}
          </div>
        )}
        {catalog.kind === "ready" && (
          <Select
            value={selectedSkill ?? undefined}
            onValueChange={(v: string) => onSelectSkill(v)}
          >
            <SelectTrigger id="skill-runner-select">
              <SelectValue placeholder="Choisir une skill…" />
            </SelectTrigger>
            <SelectContent>
              {catalog.skills.length === 0 && (
                <div className="px-2 py-1.5 text-sm opacity-70">
                  Aucune skill enregistree.
                </div>
              )}
              {catalog.skills.map((s) => (
                <SelectItem key={s.name} value={s.name}>
                  <span className="flex items-center gap-2">
                    {SKILL_ICONS[s.name] ?? <Sparkles size={14} />}
                    <span className="gsf-mono-proof text-xs">{s.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {selectedTemplate && (
          <p
            className="text-xs italic"
            style={{ color: "var(--arbitre-slate-400)" }}
          >
            {selectedTemplate.description}
          </p>
        )}
      </div>

      {/* --- Payload editor --- */}
      {selectedSkill && (
        <div className="space-y-2">
          <Label htmlFor="skill-runner-payload">
            Payload JSON
            <span
              className="ml-2 text-xs font-normal"
              style={{ color: "var(--arbitre-slate-400)" }}
            >
              (champ libre selon la skill)
            </span>
          </Label>
          <Textarea
            id="skill-runner-payload"
            value={payloadText}
            onChange={(e) => {
              setPayloadText(e.target.value);
              setPayloadError(null);
            }}
            rows={12}
            className="gsf-mono-proof text-xs"
            spellCheck={false}
            disabled={run.kind === "running"}
          />
          {payloadError && (
            <div
              className="text-xs flex items-center gap-1"
              style={{ color: "var(--reject-red)" }}
            >
              <AlertCircle size={12} /> {payloadError}
            </div>
          )}
          <div className="flex justify-end">
            <Button
              onClick={() => void onExecute()}
              disabled={
                run.kind === "running" ||
                !parsedPayload ||
                parsedPayload.kind === "err"
              }
              style={{
                background: "var(--proof-gold)",
                color: "var(--graphite-900)",
              }}
            >
              {run.kind === "running" ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-2" />
                  Execution…
                </>
              ) : (
                <>
                  <PlayCircle size={14} className="mr-2" />
                  Executer
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* --- Resultat --- */}
      {run.kind === "success" && (
        <SkillResultCard result={run.result} />
      )}
      {run.kind === "error" && (
        <div
          className="gsf-surface p-3 text-sm"
          style={{ color: "var(--reject-red)" }}
        >
          <div className="flex items-center gap-2 font-medium">
            <AlertCircle size={14} /> Echec de l'execution.
          </div>
          <div className="mt-1 ml-6">{run.detail}</div>
          {run.debug && (
            <div
              className="mt-1 ml-6 gsf-mono-proof opacity-70"
              style={{ fontSize: "0.7rem" }}
            >
              {run.debug}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sous-composant : affichage uniforme du SkillRunResponse.
// ---------------------------------------------------------------------------

function SkillResultCard({ result }: { result: SkillRunResponse }) {
  const hasBlocking = result.blocking_issues.length > 0;
  const hasWarnings = result.warnings.length > 0;
  return (
    <div className="gsf-surface p-4 space-y-3 text-sm">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {hasBlocking ? (
            <AlertCircle size={14} style={{ color: "var(--reject-red)" }} />
          ) : (
            <CheckCircle2
              size={14}
              style={{ color: "var(--validate-green)" }}
            />
          )}
          <span className="font-medium">Artifact #{result.artifact_id}</span>
          <span
            className="gsf-mono-proof text-xs"
            style={{ color: "var(--proof-gold)" }}
          >
            {result.artifact_type}
          </span>
          <span
            className="text-xs px-1.5 py-0.5 rounded"
            style={{
              background: "var(--graphite-800)",
              color: "var(--arbitre-slate-300)",
            }}
          >
            {result.status} · v{result.version}
          </span>
        </div>
        {typeof result.confidence === "number" && (
          <div
            className="text-xs flex items-center gap-1"
            style={{ color: "var(--arbitre-slate-300)" }}
          >
            confiance{" "}
            <span
              className="gsf-mono-proof font-medium"
              style={{
                color:
                  result.confidence >= 0.8
                    ? "var(--validate-green)"
                    : result.confidence >= 0.5
                    ? "var(--proof-gold)"
                    : "var(--reject-red)",
              }}
            >
              {(result.confidence * 100).toFixed(0)}%
            </span>
          </div>
        )}
      </header>

      {hasBlocking && (
        <div className="space-y-1">
          <div
            className="text-xs font-medium"
            style={{ color: "var(--reject-red)" }}
          >
            Blocages
          </div>
          <ul
            className="text-xs space-y-0.5 list-disc list-inside"
            style={{ color: "var(--arbitre-slate-200)" }}
          >
            {result.blocking_issues.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </div>
      )}

      {hasWarnings && (
        <div className="space-y-1">
          <div
            className="text-xs font-medium"
            style={{ color: "var(--proof-gold)" }}
          >
            Avertissements
          </div>
          <ul
            className="text-xs space-y-0.5 list-disc list-inside"
            style={{ color: "var(--arbitre-slate-200)" }}
          >
            {result.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <details className="text-xs">
        <summary
          className="cursor-pointer select-none"
          style={{ color: "var(--arbitre-slate-300)" }}
        >
          Contenu produit (JSON)
        </summary>
        <pre
          className="mt-2 p-2 rounded gsf-mono-proof overflow-auto max-h-80"
          style={{
            background: "var(--graphite-800)",
            color: "var(--arbitre-slate-200)",
            fontSize: "0.7rem",
            lineHeight: 1.4,
          }}
        >
          {JSON.stringify(result.content, null, 2)}
        </pre>
      </details>

      {result.audit_entry_id !== null && (
        <div
          className="text-xs"
          style={{ color: "var(--arbitre-slate-400)" }}
        >
          Entree audit chainee :{" "}
          <span className="gsf-mono-proof">#{result.audit_entry_id}</span>
        </div>
      )}
    </div>
  );
}
